import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai/react';

import { useExtensionPermissions } from '@/api/use-extension-permissions';
import { reactQueryKeys } from '@/config/react-query';
import { useProvider } from '@/hooks/use-provider';
import { authenticatedAtom } from '@/lib/store';
import { PROVIDERS } from '@/providers';
import type { LeadPhoneBook } from '@/providers/provider-types';

export const useLeadPhoneBook = () => {
  const [provider] = useProvider();
  const isAuthenticated = useAtomValue(authenticatedAtom);
  const { data: permissions } = useExtensionPermissions();

  return useQuery({
    queryKey: [
      reactQueryKeys.leadPhoneBook,
      permissions?.leadIntegration === true,
    ],
    queryFn: () => {
      if (provider.id === PROVIDERS.SALESFORCE) {
        return provider.getLeadPhoneBook();
      }
      throw new Error(
        'getLeadPhoneBook is only supported for Salesforce provider'
      );
    },
    enabled:
      isAuthenticated &&
      provider.id === PROVIDERS.SALESFORCE &&
      permissions?.leadIntegration === true,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    retry: true,
  });
};

export const useLeadName = (number?: string) => {
  const { data: leadPhoneBook } = useLeadPhoneBook();
  const { data: permissions } = useExtensionPermissions();
  if (permissions?.leadIntegration !== true) return;

  return findLeadName(leadPhoneBook, number);
};

export const findLeadName = (
  leadPhoneBook: LeadPhoneBook | undefined,
  number?: string
) => {
  if (!number) {
    return;
  }

  const directName = leadPhoneBook?.[number];
  if (directName) {
    return directName;
  }

  const leadPhoneBookArr = Object.entries(leadPhoneBook || {}).map(
    ([key, value]) => ({
      name: value,
      number: key,
    })
  );
  const match = leadPhoneBookArr.find(
    (entry) => number.endsWith(entry.number) || entry.number.endsWith(number)
  );

  return match?.name;
};
